import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { CheckCircle } from 'lucide-react';


const ImportWizard = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState('source_selection');
  const [sessionId, setSessionId] = useState(null);
  const [wizardData, setWizardData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [processingStats, setProcessingStats] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const [importStats, setImportStats] = useState(null);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [integrationStatuses, setIntegrationStatuses] = useState({
    gohighlevel: false,
    google: false,
    zoom: false
  });

  // Start wizard session when component mounts
  useEffect(() => {
    if (isOpen && !sessionId) {
      startWizard();
    }
  }, [isOpen]);

  // Check integration status when wizard starts
  const checkIntegrationStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/integrations/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        const integrations = response.data;
        setIntegrationStatuses({
          gohighlevel: integrations.gohighlevel?.connected || false,
          google: integrations.google?.connected || false,
          zoom: integrations.zoom?.connected || false
        });
        
        console.log('Integration statuses:', integrations);
      }
    } catch (error) {
      console.error('Failed to check integration status:', error);
      // Set default status if check fails
      setIntegrationStatuses({
        gohighlevel: false,
        google: false,
        zoom: false
      });
    }
  };

  const startWizard = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Check integration status first
      await checkIntegrationStatus();
      
      const token = localStorage.getItem('access_token');
      const response = await api.post(`/import-wizard/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Wizard started successfully:', response.data);
      
      // Handle both new format (with session object) and old format (direct wizard_id)
      let newSessionId, sessionData;
      if (response.data.session) {
        // New format: session object with session_id
        newSessionId = response.data.session.session_id;
        sessionData = response.data.session;
      } else {
        // Old format: direct wizard_id
        newSessionId = response.data.wizard_id;
        sessionData = response.data;
      }
      
      setSessionId(newSessionId);
      setWizardData(sessionData);
      setCurrentStep(sessionData.current_step || 'source_selection');
      
      console.log(`Session created with ID: ${newSessionId}`);
      return newSessionId; // Return the session ID for synchronous access
    } catch (error) {
      console.error('Failed to start wizard:', error);
      
      // If 401 error, provide more helpful message
      if (error.response?.status === 401) {
        setError('Session expired. Please refresh the page and try again.');
      } else {
        setError('Failed to start import wizard. Please try again.');
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Helper function to complete optional steps and move to next
  const completeStep = async (completedStep) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('access_token');
      const response = await api.post(
        `/import-wizard/${sessionId}/skip-step`,
        { step: completedStep, reason: 'User chose to skip this optional step' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWizardData(response.data.session || response.data);
      setCurrentStep(response.data.session?.current_step || response.data.current_step);
    } catch (error) {
      console.error('Failed to complete step:', error);
      setError('Failed to proceed to next step. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectSource = async (source, settings = {}) => {
    try {
      setLoading(true);
      setError('');
      
      // Check if sessionId exists, if not create one
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        console.error('SessionId is null! Attempting to start wizard first...');
        const wizardResult = await startWizard();
        // Get the session ID from state after startWizard completes
        currentSessionId = wizardResult || sessionId;
        if (!currentSessionId) {
          throw new Error('Failed to create wizard session');
        }
      }
      
      console.log(`Selecting source '${source}' with sessionId: ${currentSessionId}`);
      
      const token = localStorage.getItem('access_token');
      const response = await api.post(
        `/import-wizard/${currentSessionId}/select-source`,
        { source, settings },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWizardData(response.data.session);
      
      // Check if we should skip integration setup steps
      if (source === 'highlevel' && integrationStatuses.gohighlevel) {
        // HighLevel is already connected, go to import step  
        console.log('HighLevel already connected, proceeding to import step');
        setCurrentStep('highlevel_connection'); // This will show the import UI with Start/Skip options
        showNotification('success', 'HighLevel is connected! You can now start the import or skip to another source.');
        
      } else if (source === 'csv') {
        // Go to CSV upload
        setCurrentStep('csv_upload');
      } else if (source === 'email' && integrationStatuses.google) {
        // Google is already connected, go to email import step
        console.log('Google already connected, proceeding to email import');
        setCurrentStep('email_integration');
        showNotification('success', 'Google is connected! Ready to import email data.');
      } else if (source === 'meetings' && integrationStatuses.zoom) {
        // Zoom is already connected, go to meeting import step
        console.log('Zoom already connected, proceeding to meeting import');
        setCurrentStep('meetings_import');
        showNotification('success', 'Zoom is connected! Ready to import meeting data.');
      } else {
        // Use the next step from backend response
        setCurrentStep(response.data.session?.current_step || response.data.current_step || currentStep);
      }
      
    } catch (error) {
      console.error('Failed to select source:', error);
      setError('Failed to select import source. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const uploadFiles = async (files) => {
    try {
      setLoading(true);
      setError('');
      setUploadProgress(0);
      
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      
      Array.from(files).forEach((file, index) => {
        formData.append('files', file);
      });

      const response = await api.post(
        `/import-wizard/${sessionId}/upload-csv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      const sessionData = response.data.session || response.data;
      setWizardData(sessionData);
      setUploadedFiles(sessionData.processed_files || []);
      setCurrentStep(sessionData.current_step || currentStep);
      setUploadProgress(100);
    } catch (error) {
      console.error('Failed to upload files:', error);
      setError('Failed to upload files. Please check the format and try again.');
    } finally {
      setLoading(false);
    }
  };

  const skipStep = async (step, reason = 'User choice') => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('access_token');
      const response = await api.post(
        `/import-wizard/${sessionId}/skip-step`,
        { step, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const sessionData = response.data.session || response.data;
      setWizardData(sessionData);
      setCurrentStep(sessionData.current_step || currentStep);
    } catch (error) {
      console.error('Failed to skip step:', error);
      setError('Failed to skip step. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const connectEmail = async (provider) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('access_token');
      
      // Call the email integration endpoint based on provider
      let authUrl = '';
      if (provider === 'gmail') {
        const response = await api.get(`/integrations/google/authorize`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        authUrl = response.data.authorization_url;
      } else if (provider === 'outlook') {
        const response = await api.get(`/integrations/microsoft/authorize`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        authUrl = response.data.authorization_url;
      }
      
      if (authUrl) {
        // Open authorization in new window
        window.open(authUrl, '_blank', 'width=600,height=700,scrollbars=yes,resizable=yes');
        
        // Poll for connection status
        const pollInterval = setInterval(async () => {
          const statusResponse = await api.get(`/integrations/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const isConnected = provider === 'gmail' 
            ? statusResponse.data.google?.connected
            : statusResponse.data.microsoft365?.connected;
          
          if (isConnected) {
            clearInterval(pollInterval);
            showNotification('success', `${provider.charAt(0).toUpperCase() + provider.slice(1)} connected successfully!`);
            completeStep('email_integration_complete');
          }
        }, 2000);
        
        // Stop polling after 5 minutes
        setTimeout(() => clearInterval(pollInterval), 300000);
      }
    } catch (error) {
      console.error('Failed to connect email integration:', error);
      setError('Failed to connect email integration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const connectWorkspace = async (provider) => {
    // This is called from the workspace selection buttons - should show workspace progress
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('access_token');
      
      // Step 1: Folder setup
      showNotification('success', 'Setting up workspace folders...');
      
      const folderResponse = await api.post(`/workspace/setup-folders/${provider}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (folderResponse.data.success) {
        showNotification('success', 'Workspace folders set up successfully!');
        
        // Step 2: Email import
        showNotification('success', 'Importing client emails...');
        
        const emailResponse = await api.post(`/workspace/import-emails/${provider}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (emailResponse.data.success) {
          showNotification('success', 
            `Successfully imported ${emailResponse.data.details?.emails_imported || 0} client emails! ` +
            'Emails have been added to your knowledge base for AI analysis.'
          );
          
          // Advance to Zoom step (not back to workspace selection)
          setTimeout(() => {
            setCurrentStep('meetings_import');
          }, 1500);
          
        } else {
          setError(emailResponse.data.message || 'Email import failed');
        }
      } else {
        setError(folderResponse.data.message || 'Failed to set up workspace folders');
      }
    } catch (error) {
      console.error('Workspace integration error:', error);
      setError(error.response?.data?.detail || error.message || 'Workspace integration failed');
    } finally {
      setLoading(false);
    }
  };

  const connectZoom = async () => {
    // Show processing view with Zoom-specific progress steps
    try {
      setError('');
      
      const token = localStorage.getItem('access_token');
      
      // Move to processing and show Zoom-specific progress
      setCurrentStep('processing');
      setProcessing(true);
      setImportProgress(25);
      setImportStatus('Checking Zoom meetings for client participants...');
      
      const zoomResponse = await api.post(`/zoom/import-meetings`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (zoomResponse.data.success) {
        setImportProgress(75);
        setImportStatus('Processing meeting transcripts...');
        
        // Set Zoom-specific stats
        setImportStats({
          transcripts_imported: zoomResponse.data.details?.transcripts_imported || 0,
          meetings_with_clients: zoomResponse.data.details?.meetings_with_clients || 0,
          total_meetings_checked: zoomResponse.data.details?.total_meetings_checked || 0,
          client_contacts: zoomResponse.data.details?.client_contacts || 0
        });
        
        setTimeout(() => {
          setImportProgress(100);
          setImportStatus('Zoom import completed successfully!');
          setProcessingStats({
            transcripts_imported: zoomResponse.data.details?.transcripts_imported || 0,
            meetings_processed: zoomResponse.data.details?.meetings_with_clients || 0,
            total_meetings: zoomResponse.data.details?.total_meetings_checked || 0,
            ai_ready: true
          });
          setProcessing(false);
          
          // Auto-advance to completion
          setTimeout(() => {
            setCurrentStep('completion');
          }, 1500);
        }, 1000);
        
      } else {
        throw new Error(zoomResponse.data.message || 'Zoom import failed');
      }
    } catch (error) {
      console.error('Zoom import error:', error);
      setError(`Zoom import failed: ${error.message || 'Please try again.'}`);
      setProcessing(false);
      setCurrentStep('meetings_import'); // Return to Zoom step on error
    }
  };

  const uploadMeetingFiles = async (files) => {
    try {
      setLoading(true);
      setError('');
      setUploadProgress(0);
      setImportStatus('Uploading meeting files...');
      
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      
      Array.from(files).forEach((file) => {
        formData.append('files', file);
      });

      const response = await api.post(
        `/import-wizard/${sessionId}/upload-meetings`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
            setImportStatus(`Uploading meeting files... ${progress}%`);
          }
        }
      );

      const sessionData = response.data.session || response.data;
      setWizardData(sessionData);
      setCurrentStep(sessionData.current_step || currentStep);
      setUploadProgress(100);
      setImportStatus('Meeting files uploaded successfully!');
      showNotification('success', `${files.length} meeting files uploaded successfully`);
    } catch (error) {
      console.error('Failed to upload meeting files:', error);
      setError('Failed to upload meeting files. Please try again.');
      setImportStatus('Upload failed');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const uploadDocumentFiles = async (files) => {
    try {
      setLoading(true);
      setError('');
      setUploadProgress(0);
      setImportStatus('Uploading documents...');
      
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      
      Array.from(files).forEach((file) => {
        formData.append('files', file);
      });

      const response = await api.post(
        `/import-wizard/${sessionId}/upload-documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
            setImportStatus(`Uploading documents... ${progress}%`);
          }
        }
      );

      const sessionData = response.data.session || response.data;
      setWizardData(sessionData);
      setCurrentStep(sessionData.current_step || currentStep);
      setUploadProgress(100);
      setImportStatus('Documents uploaded successfully!');
      showNotification('success', `${files.length} documents uploaded successfully`);
    } catch (error) {
      console.error('Failed to upload document files:', error);
      setError('Failed to upload document files. Please try again.');
      setImportStatus('Upload failed');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async (templateType) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(
        `/import-wizard/templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const template = response.data.templates[templateType];
      if (template) {
        // Generate CSV content
        const csvContent = [
          template.columns.join(','),
          template.sample_data.map(row => 
            template.columns.map(col => `"${row[col] || ''}"`).join(',')
          ).join('\n')
        ].join('\n');

        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = template.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download template:', error);
      setError('Failed to download template. Please try again.');
    }
  };

  const showNotification = (type, message) => {
    setNotification({
      show: true,
      type: type === 'info' ? 'success' : type, // Convert 'info' to 'success' to show green
      message
    });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 5000);
  };

  // Status polling is now integrated into processDataForAI function

  const processDataForAI = async () => {
    console.log('🔍 processDataForAI called, sessionId:', sessionId);
    // API constant removed: use axios base path via api utility.
    
    if (!sessionId) {
      console.error('❌ Cannot start import: sessionId is null or undefined');
      setError('Import session not found. Please refresh and try again.');
      return;
    }
    
    try {
      const fullUrl = `/import-wizard/${sessionId}/process`;
      console.log('🚀 Starting HighLevel import process for session:', sessionId);
      console.log('🚀 Full API URL:', fullUrl);
      setProcessing(true);
      setError('');
      setImportProgress(5);
      setImportStatus('Starting data import...');
      
      const token = localStorage.getItem('access_token');
      console.log('📡 Making API call to /import-wizard/process...');
      console.log('📡 Authorization token present:', !!token);

      // Preflight token health for HighLevel to avoid stale JWT import failures.
      try {
        await api.post('/integrations/gohighlevel/token-health', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (healthErr) {
        console.warn('GHL token health preflight failed:', healthErr?.response?.data || healthErr?.message);
      }
      
      // Start the background import
      const response = await api.post(fullUrl, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Process endpoint response:', response.data);
      console.log('✅ Response status:', response.status);

      // Be tolerant of backend variants: some builds may return success without processing_status.
      if (response.data.success) {
        // Import started in background, now poll for status
        console.log('✅ Import started, beginning status polling...');
        setImportStatus('Processing HighLevel data...');
        setImportProgress(10);
        
        // Start polling for real-time progress
        let pollErrorCount = 0;
        const MAX_POLL_ERRORS = 5;
        
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await api.get(`/import-wizard/${sessionId}/status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            // Reset error count on successful poll
            pollErrorCount = 0;
            
            const session = statusResponse.data.session;
            console.log('📊 Import status:', session.processing_status, session.processing_stats);
            
            // Update progress from backend
            const stats = session.processing_stats || {};
            if (stats.progress !== undefined) {
              setImportProgress(stats.progress);
            }
            if (stats.stage) {
              setImportStatus(stats.stage);
            }
            
            // Check completion status
            if (session.processing_status === 'completed') {
              clearInterval(pollInterval);
              setImportProgress(100);
              setImportStatus('Import completed successfully!');
              setProcessing(false);
              
              // Merge stats from both processing_stats and import_stats
              const importStatsData = session.import_stats || {};
              const processingStatsData = session.processing_stats || stats;
              
              // Update final stats with data from both sources
              setProcessingStats(processingStatsData);
              setImportStats({
                contacts_imported: importStatsData.contacts_created || processingStatsData.contacts_processed || 0,
                opportunities_imported: importStatsData.tasks_created || processingStatsData.opportunities_processed || 0,
                chunks_created: importStatsData.total_records || processingStatsData.records_created || 0,
                embeddings_generated: processingStatsData.embeddings_generated || 0
              });
              
              const contactsCount = importStatsData.contacts_created || processingStatsData.contacts_processed || 0;
              const recordsCount = importStatsData.total_records || processingStatsData.records_created || 0;
              
              showNotification('success', 
                `✅ Import complete! ${contactsCount} contacts processed, ${recordsCount} records created.`
              );
              
              setCurrentStep('completion');
              
              // Refresh the page after 2 seconds to show imported clients
              setTimeout(() => {
                console.log('🔄 Refreshing page to display imported clients...');
                window.location.reload();
              }, 2000);
              
            } else if (session.processing_status === 'failed') {
              clearInterval(pollInterval);
              setImportProgress(0);
              setImportStatus('Import failed');
              setProcessing(false);
              setError(session.error || 'Import failed. Please try again.');
              showNotification('error', session.error || 'Import failed');
            }
            // If still processing, loop continues
            
          } catch (pollError) {
            console.error('Status polling error:', pollError);
            pollErrorCount++;
            
            if (pollErrorCount >= MAX_POLL_ERRORS) {
              clearInterval(pollInterval);
              setImportStatus('Lost connection to server. Please refresh and check status.');
              setImportProgress(0);
              setProcessing(false);
              setError('Connection lost while checking import status.');
              showNotification('error', 'Lost connection to server. Please refresh the page.');
            }
          }
        }, 3000); // Poll every 3 seconds
        
        // Safety timeout: stop polling after 10 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (processing) {
            setImportStatus('Import is taking longer than expected. Please refresh to check status.');
            setProcessing(false);
            showNotification('info', 'Import is taking longer than expected. Please check back shortly.');
          }
        }, 600000); // 10 minutes
        
      } else {
        const backendMsg = response?.data?.message || response?.data?.detail || JSON.stringify(response?.data || {});
        throw new Error(backendMsg || 'Failed to start import');
      }
      
    } catch (error) {
      console.error('Failed to start import:', error);
      setError(error.response?.data?.detail || 'Failed to start import. Please try again.');
      setImportStatus('Import failed');
      setImportProgress(0);
      setProcessing(false);
      showNotification('error', 'Failed to start import');
    }
  };

  const renderSourceSelection = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Primary Data Source</h2>
        <p className="text-gray-600">Start by importing your main client and contact data</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* GoHighLevel Option */}
        <div 
          onClick={() => selectSource('highlevel')}
          className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-purple-300 transition-colors cursor-pointer group"
        >
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center">
              <div className="text-4xl mr-2">🔗</div>
              {integrationStatuses.gohighlevel && (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="w-5 h-5 mr-1" />
                  <span className="text-sm font-medium">Connected</span>
                </div>
              )}
            </div>
            <h3 className="text-lg font-semibold">GoHighLevel</h3>
            <p className="text-gray-600 text-sm">
              {integrationStatuses.gohighlevel 
                ? "Import contacts with 'Athena AI' tag from your connected account"
                : "Import contacts, companies & opportunities from your GHL account"
              }
            </p>
            <div className={`${integrationStatuses.gohighlevel ? 'bg-green-50 border-green-200' : 'bg-green-50 border-green-200'} border rounded-lg p-3`}>
              <p className="text-xs text-green-800 font-medium">⭐ RECOMMENDED</p>
              <p className="text-xs text-green-700">
                {integrationStatuses.gohighlevel 
                  ? "Ready to import filtered contacts"
                  : "Most comprehensive data import"
                }
              </p>
            </div>
            <ul className="text-xs text-gray-500 text-left space-y-1">
              <li>• {integrationStatuses.gohighlevel ? "Only 'Athena AI' tagged contacts" : "Contacts & Companies"}</li>
              <li>• Opportunities & Pipelines</li>  
              <li>• Custom Fields & Tags</li>
              <li>• Contact History</li>
            </ul>
          </div>
        </div>

        {/* CSV Option */}
        <div 
          onClick={() => selectSource('csv')}
          className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-purple-300 transition-colors cursor-pointer group"
        >
          <div className="text-center space-y-4">
            <div className="text-4xl">📊</div>
            <h3 className="text-lg font-semibold">CSV/Excel Files</h3>
            <p className="text-gray-600 text-sm">
              Upload spreadsheets with your client and contact data
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800 font-medium">📁 FLEXIBLE</p>
              <p className="text-xs text-blue-700">Works with any data format</p>
            </div>
            <ul className="text-xs text-gray-500 text-left space-y-1">
              <li>• Contacts & Projects</li>
              <li>• Tasks & Notes</li>
              <li>• Custom Data Fields</li>
              <li>• Multiple file support</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="text-center text-sm text-gray-500">
        <p>Don't worry - you'll be able to add additional data sources in the next steps!</p>
      </div>
    </div>
  );

  const renderCSVUpload = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Your Data Files</h2>
        <p className="text-gray-600">Upload CSV or Excel files with your client data</p>
      </div>

      {/* File Upload Area */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-400 transition-colors">
        <input
          type="file"
          multiple
          accept=".csv,.xlsx,.xls"
          onChange={(e) => uploadFiles(e.target.files)}
          className="hidden"
          id="file-upload"
          disabled={loading}
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <div className="space-y-2">
            <div className="text-4xl text-gray-400">📁</div>
            <div className="text-lg font-medium text-gray-900">
              Click to upload or drag and drop
            </div>
            <div className="text-sm text-gray-500">
              CSV, Excel (.xlsx, .xls) files supported
            </div>
          </div>
        </label>
      </div>

      {/* Upload Progress */}
      {loading && uploadProgress > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Uploading files...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900">Uploaded Files</h3>
          {uploadedFiles.map((file, index) => (
            <div
              key={index}
              className={`border rounded-lg p-4 ${
                file.status === 'processed' 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className={file.status === 'processed' ? 'text-green-600' : 'text-red-600'}>
                      {file.status === 'processed' ? '✅' : '❌'}
                    </span>
                    <span className="font-medium">{file.filename}</span>
                    <span className="text-sm text-gray-500">({file.data_type})</span>
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    {file.record_count} records • {file.columns?.length || 0} columns
                    {file.error_count > 0 && (
                      <span className="text-red-600"> • {file.error_count} errors</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Templates */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Need a Template?</h3>
        <p className="text-sm text-gray-600 mb-3">
          Download our templates to see the expected format for your data.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {['contacts', 'projects', 'tasks', 'meetings'].map((type) => (
            <button
              key={type}
              onClick={() => downloadTemplate(type)}
              className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              📄 {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => skipStep('csv_upload', 'User chose to skip file upload')}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          disabled={loading}
        >
          Skip This Step
        </button>
        <div className="text-sm text-gray-500">
          💡 You can add more files later in Settings
        </div>
      </div>
    </div>
  );

  const renderAdditionalCSV = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Add More CSV Files?</h2>
        <p className="text-gray-600">Upload additional spreadsheets to supplement your data</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Upload More CSV */}
        <div 
          onClick={() => setCurrentStep('csv_upload')}
          className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors cursor-pointer"
        >
          <div className="text-center space-y-4">
            <div className="text-4xl">📊</div>
            <h3 className="text-lg font-semibold">Yes, Upload More Files</h3>
            <p className="text-gray-600 text-sm">
              Add more CSV/Excel files with additional data
            </p>
            <ul className="text-xs text-gray-500 text-left space-y-1">
              <li>• Additional contacts or clients</li>
              <li>• Project data</li>
              <li>• Task lists</li>
              <li>• Custom data sets</li>
            </ul>
          </div>
        </div>

        {/* Skip to Next Step */}
        <div 
          onClick={() => completeStep('additional_csv_complete')}
          className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-green-300 transition-colors cursor-pointer"
        >
          <div className="text-center space-y-4">
            <div className="text-4xl">➡️</div>
            <h3 className="text-lg font-semibold">No, Continue to Next Step</h3>
            <p className="text-gray-600 text-sm">
              I'm good with my current data, let's move on
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs text-green-800">Next: Email Integration</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmailOptional = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {integrationStatuses.google ? 'Import Email Data' : 'Connect Your Email?'}
        </h2>
        <p className="text-gray-600">
          {integrationStatuses.google 
            ? 'Import client communications and email history from your connected account'
            : 'Import client communications and email history'
          }
        </p>
      </div>

      {integrationStatuses.google ? (
        // Google is connected - show import UI
        <div className="bg-white border-2 border-green-200 rounded-lg p-6">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center">
              <div className="text-6xl mr-3">📧</div>
              <div className="flex items-center text-green-600">
                <CheckCircle className="w-6 h-6 mr-2" />
                <span className="text-lg font-medium">Google Connected</span>
              </div>
            </div>
            
            <h3 className="text-lg font-semibold">Ready to Import Email Data</h3>
            <p className="text-gray-600">
              Your Google account is connected and ready for email data import.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-blue-800">
                <strong>What we'll import:</strong>
                <ul className="mt-2 space-y-1 text-left">
                  <li>• Recent client email conversations</li>
                  <li>• Email attachments and documents</li>
                  <li>• Calendar meetings and appointments</li>
                  <li>• Contact information from Gmail</li>
                </ul>
              </div>
            </div>

            <button
              onClick={async () => {
                try {
                  setLoading(true);
                  setError('');
                  showNotification('success', 'Starting Google email data import...');
                  
                  // Move to processing step and start import
                  setCurrentStep('processing');
                  
                  // Auto-start the data processing
                  await processDataForAI();
                } catch (error) {
                  console.error('Failed to start email import:', error);
                  setError('Failed to start email data import. Please try again.');
                  setLoading(false);
                }
              }}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors"
              disabled={loading}
            >
              {loading ? 'Starting Import...' : 'Start Email Import'}
            </button>
          </div>
        </div>
      ) : (
        // Google not connected - show connection options
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Gmail */}
          <div 
            onClick={() => connectEmail('gmail')}
            className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors cursor-pointer"
          >
            <div className="text-center space-y-3">
              <div className="text-3xl">📧</div>
              <h3 className="font-semibold">Gmail</h3>
              <p className="text-xs text-gray-600">Connect Gmail account</p>
            </div>
          </div>

          {/* Outlook */}
          <div 
            onClick={() => connectEmail('outlook')}
            className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-orange-300 transition-colors cursor-pointer"
          >
            <div className="text-center space-y-3">
              <div className="text-3xl">📮</div>
              <h3 className="font-semibold">Outlook</h3>
              <p className="text-xs text-gray-600">Connect Outlook/Office 365</p>
            </div>
          </div>

          {/* Skip */}
          <div 
            onClick={() => completeStep('email_integration_complete')}
            className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors cursor-pointer"
          >
            <div className="text-center space-y-3">
              <div className="text-3xl">⏭️</div>
              <h3 className="font-semibold">Skip Email</h3>
              <p className="text-xs text-gray-600">Continue without email</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => completeStep('email_integration_complete')}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          disabled={loading}
        >
          Skip This Step
        </button>
        <div className="text-center text-sm text-gray-500">
          <p>Email integration helps AI understand client communications better</p>
        </div>
      </div>
    </div>
  );

  const renderMeetingsOptional = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {integrationStatuses.zoom ? 'Import Meeting Data' : 'Sync Meeting Records?'}
        </h2>
        <p className="text-gray-600">
          {integrationStatuses.zoom 
            ? 'Import meeting records and recordings from your connected Zoom account'
            : 'Upload meeting notes, transcripts, and recordings'
          }
        </p>
      </div>

      {integrationStatuses.zoom ? (
        // Zoom is connected - show import UI
        <div className="bg-white border-2 border-green-200 rounded-lg p-6">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center">
              <div className="text-6xl mr-3">🎥</div>
              <div className="flex items-center text-green-600">
                <CheckCircle className="w-6 h-6 mr-2" />
                <span className="text-lg font-medium">Zoom Connected</span>
              </div>
            </div>
            
            <h3 className="text-lg font-semibold">Ready to Import Meeting Data</h3>
            <p className="text-gray-600">
              Your Zoom account is connected and ready for meeting data import.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-blue-800">
                <strong>What we'll import:</strong>
                <ul className="mt-2 space-y-1 text-left">
                  <li>• Recent meeting recordings and transcripts</li>
                  <li>• Meeting participant information</li>
                  <li>• Chat messages and shared files</li>
                  <li>• Meeting schedules and attendee data</li>
                </ul>
              </div>
            </div>

            <button
              onClick={async () => {
                setLoading(true);
                try {
                  // Call Zoom-specific import function
                  await connectZoom();
                } catch (error) {
                  console.error('Failed to start Zoom import:', error);
                  setError('Failed to start Zoom import. Please try again.');
                  setLoading(false);
                }
              }}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors"
              disabled={loading}
            >
              {loading ? 'Starting Import...' : 'Start Meeting Import'}
            </button>
          </div>
        </div>
      ) : (
        // Zoom not connected - show file upload options
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Upload Meetings */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
            <div className="text-center space-y-4">
              <div className="text-4xl">🎥</div>
              <h3 className="text-lg font-semibold">Upload Meeting Files</h3>
              <input
                type="file"
                multiple
                accept=".txt,.pdf,.docx,.mp3,.mp4,.wav,.m4a"
                onChange={(e) => uploadMeetingFiles(e.target.files)}
                className="hidden"
                id="meeting-files-optional"
                disabled={loading}
              />
              <label htmlFor="meeting-files-optional" className="cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-purple-300">
                  <p className="text-sm text-gray-600">Drop files or click to upload</p>
                  <p className="text-xs text-gray-500">Audio, transcripts, notes</p>
                </div>
              </label>
              
              {/* Upload Progress for Meetings */}
              {loading && uploadProgress > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{importStatus}</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Skip */}
          <div 
            onClick={() => completeStep('meetings_import_complete')}
            className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-green-300 transition-colors cursor-pointer"
          >
            <div className="text-center space-y-4">
              <div className="text-4xl">⏭️</div>
              <h3 className="text-lg font-semibold">Skip Meetings</h3>
              <p className="text-gray-600 text-sm">
                Continue without meeting data
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-xs text-green-800">Next: Document Upload</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => completeStep('meetings_import_complete')}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          disabled={loading}
        >
          Skip This Step
        </button>
        <div className="text-center text-sm text-gray-500">
          <p>Meeting data helps AI understand client interactions and decisions</p>
        </div>
      </div>
    </div>
  );

  const renderDocumentsOptional = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Client Documents?</h2>
        <p className="text-gray-600">Add contracts, project files, and important documents</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Upload Documents */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
          <div className="text-center space-y-4">
            <div className="text-4xl">📄</div>
            <h3 className="text-lg font-semibold">Upload Documents</h3>
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg"
              onChange={(e) => uploadDocumentFiles(e.target.files)}
              className="hidden"
              id="document-files-optional"
              disabled={loading}
            />
            <label htmlFor="document-files-optional" className="cursor-pointer">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-purple-300">
                <p className="text-sm text-gray-600">Drop files or click to upload</p>
                <p className="text-xs text-gray-500">PDFs, Word docs, images</p>
              </div>
            </label>
            
            {/* Upload Progress for Documents */}
            {loading && uploadProgress > 0 && (
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{importStatus}</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Skip/Complete */}  
        <div 
          onClick={() => completeStep('document_upload_complete')}
          className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-purple-300 transition-colors cursor-pointer"
        >
          <div className="text-center space-y-4">
            <div className="text-4xl">🎉</div>
            <h3 className="text-lg font-semibold">Complete Import</h3>
            <p className="text-gray-600 text-sm">
              Finish and process all imported data
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-xs text-purple-800">Ready to start using Athena!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMeetingImport = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Import Meeting Records</h2>
        <p className="text-gray-600">Upload meeting notes, transcripts, and recordings</p>
      </div>

      <div className="space-y-4">
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            multiple
            accept=".txt,.pdf,.docx,.mp3,.mp4,.wav,.m4a"
            onChange={(e) => uploadMeetingFiles(e.target.files)}
            className="hidden"
            id="meeting-files"
            disabled={loading}
          />
          <label htmlFor="meeting-files" className="cursor-pointer">
            <div className="space-y-2">
              <div className="text-4xl text-gray-400">🎥</div>
              <div className="text-lg font-medium">Upload Meeting Files</div>
              <div className="text-sm text-gray-500">
                Audio recordings, transcripts, notes (.txt, .pdf, .docx, .mp3, .mp4, .wav)
              </div>
            </div>
          </label>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">What we can import:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Meeting transcripts and notes</li>
            <li>• Action items and decisions</li>
            <li>• Audio/video recordings for AI analysis</li>
            <li>• Client feedback and requirements</li>
          </ul>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => skipStep('meetings_import')}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Skip This Step
        </button>
      </div>
    </div>
  );

  const renderDocumentUpload = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Client Documents</h2>
        <p className="text-gray-600">Import contracts, project files, and client documents</p>
      </div>

      <div className="space-y-4">
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            multiple
            accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg"
            onChange={(e) => uploadDocumentFiles(e.target.files)}
            className="hidden"
            id="document-files"
            disabled={loading}
          />
          <label htmlFor="document-files" className="cursor-pointer">
            <div className="space-y-2">
              <div className="text-4xl text-gray-400">📄</div>
              <div className="text-lg font-medium">Upload Documents</div>
              <div className="text-sm text-gray-500">
                PDFs, Word docs, images, contracts, project files
              </div>
            </div>
          </label>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-1">📋</div>
            <div className="text-xs text-gray-600">Contracts</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-1">📊</div>
            <div className="text-xs text-gray-600">Reports</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-1">🖼️</div>
            <div className="text-xs text-gray-600">Images</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-1">📁</div>
            <div className="text-xs text-gray-600">Project Files</div>
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => skipStep('document_upload')}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Skip This Step
        </button>
      </div>
    </div>
  );

  const renderGHLImport = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Import GoHighLevel Data</h2>
        <p className="text-gray-600">Import your client contacts and data from GoHighLevel</p>
      </div>

      <div className="bg-white border-2 border-green-200 rounded-lg p-6">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center">
            <div className="text-6xl mr-3">📊</div>
            <div className="flex items-center text-green-600">
              <CheckCircle className="w-6 h-6 mr-2" />
              <span className="text-lg font-medium">GoHighLevel Connected</span>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold">Ready to Import Client Data</h3>
          <p className="text-gray-600">
            Your GoHighLevel account is connected and ready for data import.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm text-blue-800">
              <strong>What we'll import:</strong>
              <ul className="mt-2 space-y-1 text-left">
                <li>• <strong>Only contacts with 'Athena AI' tag</strong></li>
                <li>• Company information for tagged contacts</li>
                <li>• Opportunities and pipelines for tagged contacts</li>
                <li>• Custom fields and tags for imported contacts</li>
                <li>• Contact notes and activities for tagged contacts</li>
              </ul>
              <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded">
                <strong className="text-green-800">🎯 Filtered Import:</strong>
                <span className="text-green-700"> We only import contacts tagged with 'Athena AI' to respect your data preferences</span>
              </div>
            </div>
          </div>

          <button
            onClick={async () => {
              console.log('🖱️ Start Client Import button clicked');
              try {
                setLoading(true);
                setError('');
                showNotification('success', 'Starting GoHighLevel data import...');
                
                console.log('📝 Current sessionId:', sessionId);
                console.log('📝 Current step:', currentStep);
                
                // Move to processing step and start import
                setCurrentStep('processing');
                console.log('✅ Set currentStep to processing');
                
                // Auto-start the data processing
                console.log('📞 Calling processDataForAI()...');
                await processDataForAI();
                console.log('✅ processDataForAI() completed');
              } catch (error) {
                console.error('Failed to start GHL import:', error);
                setError('Failed to start data import. Please try again.');
                setLoading(false);
              }
            }}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors"
            disabled={loading}
          >
            {loading ? 'Starting Import...' : 'Start Client Import'}
          </button>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => skipStep('ghl_import', 'User chose to skip GHL import')}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          disabled={loading}
        >
          Skip This Step
        </button>
        <div className="text-sm text-gray-500">
          💡 This will import your tagged contacts and prepare them for AI analysis
        </div>
      </div>
    </div>
  );

  const renderWorkspaceSelection = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Connect Workspace Integration</h2>
        <p className="text-gray-600">Select your workspace provider to import calendars and documents related to existing contacts</p>
      </div>

      {/* Workspace Provider Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Google Workspace */}
        <div className="bg-white border-2 border-green-200 rounded-lg p-6">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center">
              <div className="text-6xl mr-3">📧</div>
              <div className="flex items-center text-green-600">
                <CheckCircle className="w-6 h-6 mr-2" />
                <span className="text-lg font-medium">Google Workspace</span>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              Gmail conversations, Calendar events, and Drive documents are now imported automatically
            </p>
            <div className="space-y-2 text-sm bg-blue-50 p-4 rounded border border-blue-200">
              <div className="flex items-center text-blue-700 font-medium">
                <span className="mr-2">✨</span>
                <span>Automatic Import Enabled</span>
              </div>
              <div className="text-blue-600 text-xs space-y-1">
                <div>• Initial import: 1000 emails + 100 calendar events</div>
                <div>• Real-time sync via webhooks</div>
                <div>• Backup polling every 15 minutes</div>
                <div>• Only client/contact-related data</div>
              </div>
            </div>
            
            <button
              onClick={() => {
                // Skip to next step since import is automatic
                setCurrentStep('meetings_import');
              }}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors"
            >
              Continue (Import Running in Background)
            </button>
          </div>
        </div>

        {/* Office 365 */}
        <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center">
              <div className="text-6xl mr-3">🏢</div>
              <div className="flex items-center text-blue-600">
                <CheckCircle className="w-6 h-6 mr-2" />
                <span className="text-lg font-medium">Office 365</span>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              Import Outlook conversations, Calendar events, and OneDrive documents for existing contacts
            </p>
            <div className="space-y-1 text-xs text-blue-600 bg-blue-50 p-3 rounded">
              <div>✓ Outlook conversations (to/from client contacts)</div>
              <div>✓ Calendar events and meetings</div>
              <div>✓ OneDrive shared documents</div>
              <div>✓ Teams meeting records</div>
            </div>
            
            <button
              onClick={async () => {
                setLoading(true);
                try {
                  // Move to processing step and start import (like GoHighLevel)
                  setCurrentStep('processing');
                  
                  // Start workspace import automatically
                  await connectWorkspace('office365');
                } catch (error) {
                  console.error('Failed to start Office 365 import:', error);
                  setError('Failed to start Office 365 import. Please try again.');
                  setLoading(false);
                }
              }}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors"
              disabled={loading}
            >
              {loading ? 'Starting Import...' : 'Start Office 365 Import'}
            </button>
          </div>
        </div>

        {/* Skip Option */}
        <div 
          onClick={() => skipStep('workspace_selection', 'User chose to skip workspace integration')}
          className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors cursor-pointer group"
        >
          <div className="text-center space-y-4">
            <div className="text-4xl">⏭️</div>
            <h3 className="text-lg font-semibold group-hover:text-gray-600 transition-colors">Skip for Now</h3>
            <p className="text-gray-600 text-sm">
              Skip workspace integration and proceed with current data
            </p>
            <div className="space-y-1 text-xs text-gray-500">
              <div>• Continue without workspace data</div>
              <div>• Can be added later in Settings</div>
              <div>• Focus on current contacts</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => skipStep('workspace_selection', 'User chose to skip workspace integration')}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          disabled={loading}
        >
          Skip This Step
        </button>
        <div className="text-sm text-gray-500">
          💡 Workspace data helps AI understand client interactions and project context
        </div>
      </div>
    </div>
  );

  const renderProcessing = () => (
    <div className="text-center space-y-6">
      <div className="max-w-md mx-auto">
        <div className="text-2xl font-bold text-gray-900 mb-2">Importing Your Client Data</div>
        <p className="text-gray-600 mb-6">
          We're importing your data from connected sources and preparing it for AI-powered insights.
        </p>
        
        <div className="space-y-6">
          {/* Always show processing state (like GoHighLevel) */}
          <div className="space-y-4">
            {/* Green Progress Bar (like GoHighLevel) */}
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className="bg-green-600 h-4 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${importProgress || 75}%` }}
              ></div>
            </div>
            
            {/* Progress Steps - Show current import type */}
            <div className="space-y-3">
              {/* Step 1 - Always completed for both imports */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-green-700 font-medium">
                    {importStats?.transcripts_imported !== undefined ? 'Zoom meetings checked' : 'Workspace folders set up'}
                  </span>
                </div>
                <span className="text-green-600">✓</span>
              </div>
              
              {/* Step 2 - Current import progress */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    importProgress > 75 ? 'bg-green-500' : 'bg-green-500 animate-pulse'
                  }`}>
                    {importProgress > 75 ? (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <span className={`text-green-700 ${importProgress > 75 ? 'font-medium' : ''}`}>
                    {importStatus || (importStats?.transcripts_imported !== undefined ? 'Importing meeting transcripts...' : 'Importing client emails...')}
                  </span>
                </div>
                <span className="text-green-600">
                  {importProgress > 75 ? '✓' : '⏳'}
                </span>
              </div>
            </div>
            
            {/* Progress Percentage */}
            <div className="text-center">
              <span className="text-lg font-semibold text-green-600">{importProgress || 75}%</span>
              <p className="text-sm text-gray-500 mt-1">Import in progress...</p>
            </div>
            
            {/* Loading Animation */}
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          </div>
          
          {/* Import Stats - Dynamic based on import type */}
          {importStats && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {importStats.transcripts_imported !== undefined ? (
                  /* Zoom Import Stats */
                  <>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{importStats.transcripts_imported || 0}</div>
                      <div className="text-green-800">Transcripts</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{importStats.total_meetings_checked || 0}</div>
                      <div className="text-green-800">Meetings</div>
                    </div>
                  </>
                ) : (
                  /* Workspace Import Stats */
                  <>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{importStats.emails_imported || 0}</div>
                      <div className="text-green-800">Emails</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{importStats.contacts_processed || 0}</div>
                      <div className="text-green-800">Contacts</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          
          {processingStats && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
              <div className="font-medium mb-2">Processing Complete!</div>
              <div className="space-y-1">
                <div>✓ Emails imported: {processingStats.emails_imported || 0}</div>
                <div>✓ Documents processed: {processingStats.documents_processed || 0}</div>
                <div>✓ AI analysis: Ready</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderCompletion = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Import Complete!</h2>
        <p className="text-gray-600">Your client data has been successfully imported</p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-green-900 mb-3">Import Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-green-700">Files Processed:</span>
            <span className="ml-2 font-semibold">{uploadedFiles.length}</span>
          </div>
          <div>
            <span className="text-green-700">Records Imported:</span>
            <span className="ml-2 font-semibold">{wizardData.import_stats?.total_records || 0}</span>
          </div>
          <div>
            <span className="text-green-700">Clients Created:</span>
            <span className="ml-2 font-semibold">{wizardData.import_stats?.contacts_created || 0}</span>
          </div>
          <div>
            <span className="text-green-700">Projects Created:</span>
            <span className="ml-2 font-semibold">{wizardData.import_stats?.projects_created || 0}</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">What's Next?</h3>
        <div className="space-y-2 text-sm text-gray-600">
          <p>🤖 Athena is generating AI-powered Core Briefs for your clients</p>
          <p>📊 Your dashboard is being populated with client intelligence</p>
          <p>💬 You can now ask Athena questions about your clients</p>
        </div>
      </div>

      <button
        onClick={() => {
          onComplete();
          onClose();
        }}
        className="w-full bg-purple-600 text-white py-3 px-4 rounded-md hover:bg-purple-700 transition-colors"
      >
        Explore Your Data
      </button>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'source_selection':
        return renderSourceSelection();
      case 'csv_upload':
        return renderCSVUpload();
      case 'highlevel_connection':
        return renderGHLImport();
      case 'workspace_selection':
        return renderWorkspaceSelection();
      case 'additional_csv':
        return renderAdditionalCSV();
      case 'email_integration':
        return renderEmailOptional();
      case 'meetings_import':
        return renderMeetingsOptional();
      case 'document_upload':
        return renderDocumentsOptional();
      case 'processing':
        return renderProcessing();
      case 'completion':
        return renderCompletion();
      default:
        return <div>Loading...</div>;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Import Wizard</h1>
              <p className="text-sm text-gray-500 mt-1">
                Import your client data into Athena AI
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
              <div>{error}</div>
              {/(invalid jwt|invalid_grant|expired|reconnect)/i.test(error) && (
                <div className="mt-3">
                  <button
                    onClick={() => { window.location.href = '/settings?tab=integrations'; }}
                    className="px-3 py-1.5 rounded bg-red-600 text-white text-sm hover:bg-red-700"
                  >
                    Reconnect GoHighLevel
                  </button>
                </div>
              )}
            </div>
          )}
          
          {notification.show && (
            <div className={`mb-4 px-4 py-3 rounded-md ${
              notification.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {notification.message}
            </div>
          )}
          
          {renderCurrentStep()}
        </div>
      </div>
    </div>
  );
};

export default ImportWizard;